// d:\serverN8N\Webs\webCapturas2\src\components\AccountingEntries.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import useSupabaseClient from '../hooks/useSupabaseClient';
import AccountingEntryFormModal from './AccountingEntryFormModal';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import useMediaQuery from '@mui/material/useMediaQuery';
import InvoiceAdvancesSection from './InvoiceAdvancesSection';
import EACrossModal from './EACrossModal';
import { isSupplierAdvance, removeSupplierAdvance } from '../lib/supplierAdvanceRemoval';
import { isDemoAccount, readPresentationUser, formatColombianDate, formatColombianCurrency, explainDemoObservation } from '../lib/demoAccount.mjs';
import ColombianDateField from './ColombianDateField';
const filterOptionsLimit = createFilterOptions({
  limit: 50,
  stringify: (option) => typeof option === 'string' ? option : `${option.codigo} ${option.descripcion}`
});

const getColombianAccountingRank = (accountCode, isDebit) => {
  const code = String(accountCode || '');
  if (code.startsWith('5') || code.startsWith('6') || code.startsWith('7')) return 10;
  if (code.startsWith('1')) return 20;
  if (code.startsWith('2408') && isDebit) return 30; // IVA Descontable
  if (code.startsWith('2365') || code.startsWith('2367') || code.startsWith('2368')) return 40; // Retenciones
  if ((code.startsWith('2205') || code.startsWith('2335') || code.startsWith('2')) && !isDebit) return 50; // CxP
  if (code.startsWith('4')) return 60; // Ingresos
  return 100; // Otros
};

const AccountingEntries = ({ invoiceId, showFinalizeButton = false, onFinalize, finalizeError, erpListsStore }) => {
  const demoPresentation = isDemoAccount(readPresentationUser());
  const { supabase } = useSupabaseClient();
  const isCompactViewport = useMediaQuery('(max-width:1100px), (max-height:900px)');
  const [entries, setEntries] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentEntry, setCurrentEntry] = useState(null); // For editing

  // Estados para Drag & Drop
  const [draggedItemIndex, setDraggedItemIndex] = useState(null);
  const [dragOverItemIndex, setDragOverItemIndex] = useState(null);

  // Estado para advertencia de descuadre
  const [balanceWarning, setBalanceWarning] = useState('');
  const [balanceDiff, setBalanceDiff] = useState(0);
  
  // Estado para edición rápida de descripciones
  const [editingDescriptionId, setEditingDescriptionId] = useState(null);
  const [editingDescriptionValue, setEditingDescriptionValue] = useState('');
  const [isSavingDescription, setIsSavingDescription] = useState(false);

  // Estado para cruce de EA
  const [isEAModalOpen, setIsEAModalOpen] = useState(false);
  const [currentInvoiceData, setCurrentInvoiceData] = useState(null);

  // Estado para acciones masivas
  const [bulkDescription, setBulkDescription] = useState('');
  const [bulkCostCenter, setBulkCostCenter] = useState(null);
  const [bulkDepartment, setBulkDepartment] = useState(null);

  const [bulkCcSearchText, setBulkCcSearchText] = useState('');
  const [bulkCcOptions, setBulkCcOptions] = useState([]);
  const [isSearchingBulkCc, setIsSearchingBulkCc] = useState(false);
  const [bulkCcAutocompleteOpen, setBulkCcAutocompleteOpen] = useState(false);

  const [bulkDeptSearchText, setBulkDeptSearchText] = useState('');
  const [bulkDeptOptions, setBulkDeptOptions] = useState([]);
  const [isSearchingBulkDept, setIsSearchingBulkDept] = useState(false);
  const [bulkDeptAutocompleteOpen, setBulkDeptAutocompleteOpen] = useState(false);

  const [bulkProject, setBulkProject] = useState(null);
  const [bulkProjectSearchText, setBulkProjectSearchText] = useState('');
  const [bulkProjectOptions, setBulkProjectOptions] = useState([]);
  const [isSearchingBulkProject, setIsSearchingBulkProject] = useState(false);
  const [bulkProjectAutocompleteOpen, setBulkProjectAutocompleteOpen] = useState(false);

  const [bulkActivity, setBulkActivity] = useState(null);
  const [bulkActivitySearchText, setBulkActivitySearchText] = useState('');
  const [bulkActivityOptions, setBulkActivityOptions] = useState([]);
  const [isSearchingBulkActivity, setIsSearchingBulkActivity] = useState(false);
  const [bulkActivityAutocompleteOpen, setBulkActivityAutocompleteOpen] = useState(false);

  const [bulkDestino, setBulkDestino] = useState('');
  const [bulkDate, setBulkDate] = useState('');
  const [isBulkUpdating, setIsBulkUpdating] = useState(false);

  // Estado para configuración del tenant
  const [tenantConfig, setTenantConfig] = useState(null);

  // Ref para evitar race-conditions en StrictMode al aplicar anticipos automáticamente
  const isAutoApplyingRef = useRef(false);

  useEffect(() => {
    if (invoiceId && supabase) {
      supabase
        .from('invoices')
        .select('id, num_identificacion, billing_name')
        .eq('id', invoiceId)
        .single()
        .then(({ data }) => {
          if (data) setCurrentInvoiceData(data);
        });
    }
  }, [invoiceId, supabase]);

  // Función para calcular el balance
  const calculateBalance = (list = entries) => {
    const totalDebit = list.reduce((acc, e) => acc + (Number(e.debit) || 0), 0);
    const totalCredit = list.reduce((acc, e) => acc + (Number(e.credit) || 0), 0);
    return { totalDebit, totalCredit, diff: totalDebit - totalCredit };
  };

  // Actualiza advertencia en tiempo real
  useEffect(() => {
    const { totalDebit, totalCredit, diff } = calculateBalance();
    if (entries.length > 0 && Math.abs(diff) > 0.01) {
      setBalanceDiff(diff);
      setBalanceWarning(`El asiento está descuadrado. Débitos: ${totalDebit.toLocaleString('es-CO', { style: 'currency', currency: 'COP' })} | Créditos: ${totalCredit.toLocaleString('es-CO', { style: 'currency', currency: 'COP' })} (Diferencia: ${Math.abs(diff).toLocaleString('es-CO', { style: 'currency', currency: 'COP' })})`);
    } else {
      setBalanceDiff(0);
      setBalanceWarning('');
    }
  }, [entries]);

  const searchCostCenters = async (term) => {
    if (!supabase) return [];
    const t = String(term || '').trim();
    const storedUser = localStorage.getItem('invoice_user');
    if (!storedUser) return [];
    const user = JSON.parse(storedUser);
    if (!user?.id) return [];

    try {
      let query = supabase.from('invoice_erp_cost_centers').select('codigo, descripcion').eq('tenant_id', user.id).limit(50);
      if (t) {
        query = query.or(`codigo.ilike.%${t}%,descripcion.ilike.%${t}%`);
      }
      const { data, error } = await query;
      if (error) throw error;
      return data || [];
    } catch (err) {
      console.error("Error searching cost centers:", err);
      return [];
    }
  };

  const searchDepartments = async (term) => {
    if (!supabase) return [];
    const t = String(term || '').trim();
    const storedUser = localStorage.getItem('invoice_user');
    if (!storedUser) return [];
    const user = JSON.parse(storedUser);
    if (!user?.id) return [];

    try {
      let query = supabase.from('invoice_erp_departments').select('codigo, descripcion').eq('tenant_id', user.id).limit(50);
      if (t) {
        query = query.or(`codigo.ilike.%${t}%,descripcion.ilike.%${t}%`);
      }
      const { data, error } = await query;
      if (error) throw error;
      return data || [];
    } catch (err) {
      console.error("Error searching departments:", err);
      return [];
    }
  };

  useEffect(() => {
    setIsSearchingBulkCc(true);
    const t = setTimeout(async () => {
      const opts = await searchCostCenters(bulkCcSearchText);
      setBulkCcOptions(opts);
      setIsSearchingBulkCc(false);
    }, 300);
    return () => clearTimeout(t);
  }, [bulkCcSearchText, supabase]);

  useEffect(() => {
    setIsSearchingBulkDept(true);
    const t = setTimeout(async () => {
      const opts = await searchDepartments(bulkDeptSearchText);
      setBulkDeptOptions(opts);
    setIsSearchingBulkDept(false);
    }, 300);
    return () => clearTimeout(t);
  }, [bulkDeptSearchText, supabase]);

  const searchProjects = async (term) => {
    if (!supabase) return [];
    const t = String(term || '').trim();
    const storedUser = localStorage.getItem('invoice_user');
    if (!storedUser) return [];
    const user = JSON.parse(storedUser);
    if (!user?.id) return [];

    try {
      let query = supabase.from('invoice_erp_projects').select('codigo, descripcion').eq('tenant_id', user.id).limit(50);
      if (t) {
        query = query.or(`codigo.ilike.%${t}%,descripcion.ilike.%${t}%`);
      }
      const { data, error } = await query;
      if (error) throw error;
      return data || [];
    } catch (err) {
      console.error("Error searching projects:", err);
      return [];
    }
  };

  const searchActivities = async (term) => {
    if (!supabase) return [];
    const t = String(term || '').trim();
    const storedUser = localStorage.getItem('invoice_user');
    if (!storedUser) return [];
    const user = JSON.parse(storedUser);
    if (!user?.id) return [];

    try {
      let query = supabase.from('invoice_erp_activities').select('codigo, descripcion').eq('tenant_id', user.id).limit(50);
      if (t) {
        query = query.or(`codigo.ilike.%${t}%,descripcion.ilike.%${t}%`);
      }
      const { data, error } = await query;
      if (error) throw error;
      return data || [];
    } catch (err) {
      console.error("Error searching activities:", err);
      return [];
    }
  };

  useEffect(() => {
    setIsSearchingBulkProject(true);
    const t = setTimeout(async () => {
      const opts = await searchProjects(bulkProjectSearchText);
      setBulkProjectOptions(opts);
      setIsSearchingBulkProject(false);
    }, 300);
    return () => clearTimeout(t);
  }, [bulkProjectSearchText, supabase]);

  useEffect(() => {
    setIsSearchingBulkActivity(true);
    const t = setTimeout(async () => {
      const opts = await searchActivities(bulkActivitySearchText);
      setBulkActivityOptions(opts);
      setIsSearchingBulkActivity(false);
    }, 300);
    return () => clearTimeout(t);
  }, [bulkActivitySearchText, supabase]);

  const fetchTenantConfig = useCallback(async (userId) => {
    try {
        const { data, error } = await supabase
          .from('invoice_config')
        .select('enable_destino_field, enable_advances_sync, auto_apply_advances, ea_bridge_account, enable_ea_sync')
          .eq('user_id', userId)
          .single();
      
      if (!error && data) {
        setTenantConfig(data);
      }
    } catch (err) {
      console.error('Error fetching tenant config:', err);
    }
  }, [supabase]);

  const handleAutoBalanceCxP = async () => {
    if (Math.abs(balanceDiff) < 0.01) return;
    
    // Identificar cuenta transitoriamente de la CxP del emisor
    const cxpEntry = entries.find(e => {
        const code = String(e.account_code || '');
        const desc = String(e.description || '').toLowerCase();
        return code.startsWith('2205') || code.startsWith('2335') || desc.includes('proveedor principal');
    });

    if (!cxpEntry) {
        alert('No se detectó una cuenta de CxP principal (2205 / 2335) para asentar la diferencia.');
        return;
    }

    const currentCredit = Number(cxpEntry.credit) || 0;
    const currentDebit = Number(cxpEntry.debit) || 0;
    
    let newCredit = currentCredit;
    let newDebit = currentDebit;
    
    // Si la diferencia es positiva, hay más débito que crédito, sumamos al crédito de la cuenta CxP.
    // Si la cuenta históricamente se mueve al lado débito por un abono (factura rectificativa), se ajustará matemáticamente.
    if (currentCredit > 0 || currentDebit === 0) {
        newCredit = currentCredit + balanceDiff;
    } else {
        newDebit = currentDebit - balanceDiff;
    }

    setIsLoading(true);
    try {
        const { error: updateError } = await supabase
          .from('accounting_entries')
          .update({ credit: newCredit, debit: newDebit })
          .eq('id', cxpEntry.id);
          
        if (updateError) throw updateError;
        
        await fetchEntries();
    } catch (err) {
        console.error('Error auto-balancing:', err);
        alert('Error al auto-cuadrar el asiento: ' + err.message);
    } finally {
        setIsLoading(false);
    }
  };

  const autoApplyAdvances = async (_currentEntries, user, config) => {
    if (!config?.enable_advances_sync || !config?.auto_apply_advances || isAutoApplyingRef.current) return false;
    isAutoApplyingRef.current = true;
    try {
      const { data, error } = await supabase.rpc('invoice_auto_apply_supplier_advances', {
        p_user_id: user.id, p_invoice_id: invoiceId
      });
      if (error) throw error;
      return Number(data) > 0;
    } catch (err) {
      setError('No se aplicaron anticipos automáticamente: ' + err.message);
      return false;
    }
  };

  const fetchEntries = useCallback(async () => {
    if (!invoiceId) {
        setEntries([]);
        return;
    }
    setIsLoading(true);
    setError('');
    try {
      // Get current user from localStorage or context
      const storedUser = localStorage.getItem('invoice_user');
      if (!storedUser) {
        throw new Error('User not authenticated');
      }
      const user = JSON.parse(storedUser);
      
      if (!tenantConfig) {
        await fetchTenantConfig(user.id);
      }
      
      const { data, error: fetchError } = await supabase
        .from('accounting_entries')
        .select('*')
        .eq('invoice_id', invoiceId)
        .eq('user_id', user.id)
        .order('order_index', { ascending: true })
        .order('entry_date', { ascending: true })
        .order('id', { ascending: true });

      if (fetchError) throw fetchError;
      
      let fetchedEntries = data || [];
      const needsOrdering = fetchedEntries.some(e => !e.order_index || e.order_index === 0);
      
      if (needsOrdering && fetchedEntries.length > 0) {
        const orderedItems = fetchedEntries.filter(e => e.order_index > 0).sort((a, b) => a.order_index - b.order_index);
        const unorderedItems = fetchedEntries.filter(e => !e.order_index || e.order_index === 0);
        
        let finalArray = [...orderedItems];
        unorderedItems.forEach(newItem => {
           const newRank = getColombianAccountingRank(newItem.account_code, (Number(newItem.debit) || 0) > 0);
           let insertIndex = finalArray.findIndex(item => {
               const itemRank = getColombianAccountingRank(item.account_code, (Number(item.debit) || 0) > 0);
               return itemRank > newRank;
           });
           
           if (insertIndex === -1) {
               finalArray.push(newItem);
           } else {
               finalArray.splice(insertIndex, 0, newItem);
           }
        });
        
        finalArray.forEach((item, index) => {
           item.order_index = (index + 1) * 10;
        });
        fetchedEntries = finalArray;
        
        const updatePromises = finalArray.map(item => 
            supabase.from('accounting_entries').update({ order_index: item.order_index }).eq('id', item.id)
        );
        Promise.all(updatePromises).catch(err => console.error("Error auto-saving order", err));
      }
      
      // Auto-aplicación de anticipos
      let didAutoApply = false;
      if (tenantConfig?.enable_advances_sync && fetchedEntries.length > 0) {
        didAutoApply = await autoApplyAdvances(fetchedEntries, user, tenantConfig);
      }

      if (didAutoApply) {
        // Recargar las entradas si auto-aplicamos algo
        const { data: freshData } = await supabase
          .from('accounting_entries')
          .select('*')
          .eq('invoice_id', invoiceId)
          .eq('user_id', user.id)
          .order('order_index', { ascending: true })
          .order('entry_date', { ascending: true })
          .order('id', { ascending: true });
        if (freshData) {
          fetchedEntries = freshData;
        }
      }

      setEntries(fetchedEntries);
    } catch (fetchError) {
      console.error('Error fetching accounting entries:', fetchError);
      setError(`Error al cargar asientos: ${fetchError.message}`);
      setEntries([]);
    } finally {
      setIsLoading(false);
    }
  }, [invoiceId, fetchTenantConfig, tenantConfig, supabase]);

  useEffect(() => {
    isAutoApplyingRef.current = false; // Reset auto-apply lock when opening a new invoice
  }, [invoiceId]);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  const handleOpenModalToAdd = () => {
    setCurrentEntry(null);
    setIsModalOpen(true);
  };

  const handleOpenModalToEdit = (entry) => {
    setCurrentEntry(entry);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setCurrentEntry(null);
  };

  const handleSaveEntry = (_savedEntry) => {
    // Refresh the list after saving
    fetchEntries();
    handleCloseModal();
  };

  const handleDeleteEntry = async (entryId) => {
    if (!window.confirm('¿Estás seguro de que deseas eliminar este asiento?')) {
        return;
    }
    setError('');
    setIsLoading(true);
    try {
        // Get current user from localStorage
        const storedUser = localStorage.getItem('invoice_user');
        if (!storedUser) {
          throw new Error('User not authenticated');
        }
        const user = JSON.parse(storedUser);
        
        const entryToDelete = entries.find(e => e.id === entryId);
        if (isSupplierAdvance(entryToDelete)) {
          await removeSupplierAdvance(supabase, user.id, invoiceId, entryId);
          await fetchEntries();
          return;
        }
        
        const { error: deleteError } = await supabase
            .from('accounting_entries')
            .delete()
            .eq('id', entryId)
            .eq('invoice_id', invoiceId)
            .eq('user_id', user.id);
        if (deleteError) throw deleteError;
        
        // Si el asiento era un anticipo o EA, restaurar el saldo
        if (entryToDelete && entryToDelete.advance_invc && entryToDelete.advance_cruce) {
          try {
            // Los anticipos se eliminan mediante RPC; este bloque solo restaura cruces de EA.
            if (Number(entryToDelete.debit) > 0 && tenantConfig?.enable_ea_sync) {
              const { data: eaToRestore } = await supabase
                .from('invoice_ea_entries')
                .select('*')
                .eq('tenant_id', user.id)
                .eq('id_n', entryToDelete.third_party_nit)
                .eq('tipo', entryToDelete.advance_cruce)
                .eq('number', entryToDelete.advance_invc)
                .single();

              if (eaToRestore) {
                const restoredCost = Number(eaToRestore.cost_pending) + Number(entryToDelete.debit);
                await supabase
                  .from('invoice_ea_entries')
                  .update({ cost_pending: restoredCost })
                  .eq('id', eaToRestore.id);
              }
              
              const genericEntryToRestore = entries.find(e => {
                return e.id !== entryId && Number(e.debit) > 0 && e.account_code !== tenantConfig?.ea_bridge_account;
              });

              if (genericEntryToRestore) {
                const restoredExpenseDebit = Number(genericEntryToRestore.debit) + Number(entryToDelete.debit);
                await supabase
                  .from('accounting_entries')
                  .update({ debit: restoredExpenseDebit })
                  .eq('id', genericEntryToRestore.id);
              }
            }
          } catch (restorationError) {
             console.error('Error restaurando saldo de anticipo o EA:', restorationError);
          }
        }
        
        // Refresh list completely to pull restored CxP credits if any
        await fetchEntries();
    } catch (deleteError) {
        console.error('Error deleting entry:', deleteError);
        setError(`Error al eliminar: ${deleteError.message}`);
    } finally {
        setIsLoading(false);
    }
  };

  const handleEACrossApply = async (itemsToCross) => {
    if (!itemsToCross || itemsToCross.length === 0) {
      setIsEAModalOpen(false);
      return;
    }

    setIsLoading(true);
    try {
      const storedUser = localStorage.getItem('invoice_user');
      const user = JSON.parse(storedUser);

      // Encontrar el asiento débito original que se va a rebajar
      // Idealmente, buscamos el débito que coincida con lo traído por ServiciosV2 o el mayor débito que no sea puente.
      const genericDebitEntry = entries.find(e => 
        Number(e.debit) > 0 && e.account_code !== tenantConfig?.ea_bridge_account
      );

      if (!genericDebitEntry) {
        throw new Error('No se encontró un asiento débito base para rebajar el cruce con EA.');
      }

      if (!tenantConfig?.ea_bridge_account) {
        throw new Error('No hay una cuenta puente de EA configurada. Revise la Configuración Local de Servicios.');
      }

      const totalToCross = itemsToCross.reduce((sum, item) => sum + Number(item.applied_amount), 0);
      let remainingGenericDebit = Number(genericDebitEntry.debit) - totalToCross;

      // 1. Actualizar el débito original
      const { error: updateError } = await supabase
        .from('accounting_entries')
        .update({ debit: remainingGenericDebit })
        .eq('id', genericDebitEntry.id);
      
      if (updateError) throw updateError;

      // 2. Insertar los nuevos asientos de cuenta puente
      let maxOrder = Math.max(...entries.map(e => e.order_index || 0), 0);
      
      for (const item of itemsToCross) {
        maxOrder += 10;
        const { error: insertError } = await supabase
          .from('accounting_entries')
          .insert({
            invoice_id: invoiceId,
            user_id: user.id,
            account_code: tenantConfig.ea_bridge_account,
            account_name: 'PUENTE ENTRADAS ALMACEN',
            description: `Cruce EA ${item.tipo} ${item.number}`,
            debit: item.applied_amount,
            credit: 0,
            third_party_nit: genericDebitEntry.third_party_nit,
            third_party_name: genericDebitEntry.third_party_name,
            cost_center_code: genericDebitEntry.cost_center_code,
            department_code: genericDebitEntry.department_code,
            project_code: genericDebitEntry.project_code,
            activity_code: genericDebitEntry.activity_code,
            base_amount: 0,
            tax_rate: 0,
            order_index: maxOrder,
            entry_date: genericDebitEntry.entry_date,
            advance_cruce: item.tipo, // Reutilizamos estas columnas para mapear CRUCE en CARPRODE
            advance_invc: item.number // Reutilizamos estas columnas para mapear INVC en CARPRODE
          });
        
        if (insertError) throw insertError;

        // 3. Restar el saldo de la EA en invoice_ea_entries
        const { error: eaUpdateError } = await supabase
          .from('invoice_ea_entries')
          .update({ cost_pending: Number(item.cost_pending) - item.applied_amount })
          .eq('id', item.id);
        
        if (eaUpdateError) throw eaUpdateError;

        // 4. Guardar registro en la tabla pivote para reportes o des-cruce futuro (opcional, si hay `invoice_ea_crosses`)
        // Asumo que invoice_ea_crosses existe.
        await supabase
          .from('invoice_ea_crosses')
          .insert({
            tenant_id: user.id,
            invoice_id: invoiceId,
            ea_entry_id: item.id,
            amount_crossed: item.applied_amount,
            created_at: new Date().toISOString()
          });
      }

      await fetchEntries();
      setIsEAModalOpen(false);
    } catch (err) {
      console.error('Error aplicando cruce EA:', err);
      alert(`Error aplicando cruce con EA: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateDescription = async (entryId, newDescription) => {
    setIsSavingDescription(true);
    try {
      const { error: updateError } = await supabase
        .from('accounting_entries')
        .update({ description: newDescription })
        .eq('id', entryId);
        
      if (updateError) throw updateError;
      
      setEntries(prev => prev.map(e => e.id === entryId ? { ...e, description: newDescription } : e));
      setEditingDescriptionId(null);
    } catch (err) {
      console.error('Error updating description:', err);
      alert(`Error al actualizar descripción: ${err.message}`);
    } finally {
      setIsSavingDescription(false);
    }
  };

  const handleBulkUpdate = async (field, value) => {
    if (!value || !value.trim()) {
      alert('Por favor ingresa un valor para aplicar de forma masiva.');
      return;
    }
    
    if (!window.confirm(`¿Estás seguro de que deseas aplicar "${value}" a todas las filas en el campo "${field}"?`)) {
      return;
    }

    setIsBulkUpdating(true);
    try {
      const { error: updateError } = await supabase
        .from('accounting_entries')
        .update({ [field]: value.trim() })
        .eq('invoice_id', invoiceId);
        
      if (updateError) throw updateError;
      
      // Actualizar localmente o refrescar
      await fetchEntries();
      
      // Limpiar el campo masivo después de aplicar
      if (field === 'description') setBulkDescription('');
      if (field === 'activity_code') setBulkActivity(null);
      if (field === 'cost_center_code') setBulkCostCenter(null);
      if (field === 'department_code') setBulkDepartment(null);
      if (field === 'project_code') setBulkProject(null);
      if (field === 'destino') setBulkDestino('');
      if (field === 'entry_date') setBulkDate('');
      
    } catch (err) {
      console.error(`Error in bulk update for ${field}:`, err);
      alert(`Error al actualizar masivamente: ${err.message}`);
    } finally {
      setIsBulkUpdating(false);
    }
  };
  
  // Drag and Drop Handlers
  const handleDragStart = (e, index) => {
    setDraggedItemIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    // Opcional: configurar una imagen fantasma transparente o algo sutil
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragOverItemIndex !== index) {
      setDragOverItemIndex(index);
    }
  };

  const handleDrop = async (e, index) => {
    e.preventDefault();
    if (draggedItemIndex === null || draggedItemIndex === index) {
      setDraggedItemIndex(null);
      setDragOverItemIndex(null);
      return;
    }

    const newEntries = [...entries];
    const draggedItem = newEntries[draggedItemIndex];
    newEntries.splice(draggedItemIndex, 1);
    newEntries.splice(index, 0, draggedItem);

    // Reasignar order_index
    newEntries.forEach((item, i) => {
      item.order_index = (i + 1) * 10;
    });

    setEntries(newEntries);
    setDraggedItemIndex(null);
    setDragOverItemIndex(null);

    // Guardar en backend
    try {
      const updatePromises = newEntries.map(item => 
          supabase.from('accounting_entries').update({ order_index: item.order_index }).eq('id', item.id)
      );
      await Promise.all(updatePromises);
    } catch (err) {
      console.error("Error guardando el nuevo orden", err);
      setError("Error al guardar el nuevo orden de los asientos.");
    }
  };
  
  const handleDragEnd = (e) => {
    if (e && e.target && e.target.setAttribute) {
      e.target.setAttribute('draggable', 'false');
    }
    setDraggedItemIndex(null);
    setDragOverItemIndex(null);
  };
  
  // Basic styling - you'll want to improve this
  const tableStyle = {
    width: '100%',
    minWidth: isCompactViewport ? 1650 : undefined,
    borderCollapse: 'collapse',
    marginTop: 12,
    tableLayout: 'fixed'
  };
  const thTdStyle = { border: '1px solid #ddd', padding: '6px 8px', textAlign: 'left', verticalAlign: 'top' };
  const thStyle = { ...thTdStyle, backgroundColor: '#f2f2f2', fontWeight: 600 };
  const cellEllipsisStyle = { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };
  const cellWrapStyle = { overflowWrap: 'anywhere', whiteSpace: 'normal' };
  const buttonStyle = { padding: '5px 10px', margin: '0 5px', cursor: 'pointer', border: '1px solid #ccc', borderRadius: '4px' };

  // Validación de asientos
  const validateEntries = () => {
    const errors = [];
    // Validar balance
    const { diff } = calculateBalance();
    if (entries.length > 0 && diff !== 0) {
      errors.push('El asiento está descuadrado. Débitos y créditos deben ser iguales.');
    }
    // Solo validar valores negativos
    for (const entry of entries) {
      if (Number(entry.debit) < 0) {
        errors.push(`Valor negativo en débito para cuenta ${entry.account_code}`);
      }
      if (Number(entry.credit) < 0) {
        errors.push(`Valor negativo en crédito para cuenta ${entry.account_code}`);
      }
    }
    return errors;
  };


  // Lógica para aplicar anticipos
  const cxpEntry = entries.find(e => {
    const code = String(e.account_code || '');
    const desc = String(e.description || '').toLowerCase();
    return code.startsWith('2205') || code.startsWith('2335') || desc.includes('proveedor principal');
  });

  const handleApplyAdvance = async (advance, amountToApply) => {
    if (!cxpEntry) return;
    setIsLoading(true);
    try {
      const user = JSON.parse(localStorage.getItem('invoice_user'));
      const { error } = await supabase.rpc('invoice_apply_supplier_advance', {
        p_user_id: user.id, p_invoice_id: invoiceId,
        p_advance_id: advance.id, p_amount: amountToApply, p_automatic: false
      });
      if (error) throw error;
      await fetchEntries();
      alert('Anticipo aplicado correctamente.');
    } catch (err) {
      alert('No se aplicó el anticipo: ' + err.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Manejar finalización
  const handleFinalizeClick = () => {
    const errors = validateEntries();
    if (errors.length > 0) {
      if (onFinalize) onFinalize(errors.join(' | '));
      return;
    }
    if (onFinalize) onFinalize('');
  };

  if (!invoiceId) {
    return <p>Selecciona una factura para ver su causación contable.</p>;
  }
  
  if (isLoading && !entries.length) { // Show loading only if entries are not yet loaded
    return <p>Cargando asientos contables...</p>;
  }

  return (
    <div style={{
      padding: isCompactViewport ? 4 : 8,
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      minHeight: 0,
      overflowY: 'auto',
      overflowX: 'hidden',
      scrollbarGutter: 'stable'
    }}>
      {error && (
        <div role="alert" style={{ color: '#b42318', background: '#fff1f0', padding: 12, marginBottom: 12 }}>
          <p>{error}</p>
          <button type="button" onClick={() => setError('')}>Cerrar aviso</button>
          <button type="button" disabled={isLoading} onClick={fetchEntries}>Volver a cargar</button>
        </div>
      )}
      <div style={{
        position: 'static',
        background: '#fff',
        paddingBottom: 12,
        marginBottom: 12,
        borderBottom: '1px solid #eee',
        flexShrink: 0
      }}>
        <h3 style={{marginTop: 0}}>Causación Contable de la Factura</h3>
        <InvoiceAdvancesSection
          invoiceId={invoiceId}
          cxpEntry={cxpEntry}
          onApplyAdvance={handleApplyAdvance}
          refreshTrigger={entries} // Pasa los entries para que al actualizar refresque
          enabled={tenantConfig?.enable_advances_sync}
        />
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '10px' }}>
          <button onClick={handleOpenModalToAdd} style={{...buttonStyle, backgroundColor: '#28a745', color: 'white'}}>
            Agregar Partida Contable
          </button>
          
          {tenantConfig?.enable_ea_sync && entries.some(e => Number(e.debit) > 0 && e.account_code !== tenantConfig?.ea_bridge_account) && (
            <button 
              onClick={() => setIsEAModalOpen(true)} 
              style={{...buttonStyle, backgroundColor: '#17a2b8', color: 'white'}}
            >
              Cruzar con EA (Entradas de Almacén)
            </button>
          )}
        </div>
        {balanceWarning && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginBottom: '10px' }}>
            <p style={{ color: 'orange', fontWeight: 'bold', margin: 0 }}>{balanceWarning}</p>
            <button 
              onClick={handleAutoBalanceCxP}
              style={{ padding: '6px 12px', cursor: 'pointer', border: 'none', borderRadius: '4px', backgroundColor: '#fd7e14', color: 'white', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '5px' }}
            >
              🪄 Auto-cuadrar contra CxP Proveedor
            </button>
          </div>
        )}
        {showFinalizeButton && (
          <button
            style={{
              ...buttonStyle,
              backgroundColor: '#1976d2',
              color: 'white',
              fontWeight: 'bold',
              marginLeft: isCompactViewport ? 0 : 16,
              marginBottom: isCompactViewport ? 8 : 0
            }}
            onClick={handleFinalizeClick}
          >
            Cerrar / Finalizar Asiento
          </button>
        )}
        {finalizeError && (
          <div style={{ color: '#d32f2f', background: '#fff3f3', padding: 8, borderRadius: 4, marginTop: 8, fontWeight: 'bold', maxWidth: 600 }}>
            {finalizeError.split(' | ').map((err, idx) => (
              <div key={idx}>• {err}</div>
            ))}
          </div>
        )}

        {/* Barra de Acciones Masivas */}
        <div style={{ 
          marginTop: 12, 
          padding: '12px', 
          backgroundColor: '#f8f9fa', 
          borderRadius: '8px',
          border: '1px solid #dee2e6',
          display: 'flex',
          flexWrap: 'wrap',
          gap: '16px',
          alignItems: 'flex-end'
        }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <label style={{ fontSize: '12px', fontWeight: 'bold' }}>Descripción Masiva:</label>
            <div style={{ display: 'flex', gap: '4px' }}>
              <input 
                type="text" 
                value={bulkDescription} 
                onChange={(e) => setBulkDescription(e.target.value)}
                placeholder="Ej: Consumo restaurante"
                style={{ padding: '6px', borderRadius: '4px', border: '1px solid #ccc', width: '200px' }}
              />
              <button 
                onClick={() => handleBulkUpdate('description', bulkDescription)}
                disabled={isBulkUpdating}
                style={{ ...buttonStyle, margin: 0, backgroundColor: '#6c757d', color: 'white' }}
              >
                Aplicar
              </button>
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <label style={{ fontSize: '12px', fontWeight: 'bold' }}>Fecha Masiva:</label>
            <div style={{ display: 'flex', gap: '4px' }}>
              {demoPresentation ? <ColombianDateField value={bulkDate} onChangeValue={setBulkDate}
                size="small" label="Fecha masiva" disabled={isBulkUpdating} sx={{ width: 160 }} /> : <input 
                type="date" 
                value={bulkDate} 
                onChange={(e) => setBulkDate(e.target.value)}
                style={{ padding: '6px', borderRadius: '4px', border: '1px solid #ccc', width: '130px' }}
              />}
              <button 
                onClick={() => handleBulkUpdate('entry_date', bulkDate)}
                disabled={isBulkUpdating || (demoPresentation && !bulkDate)}
                style={{ ...buttonStyle, margin: 0, backgroundColor: '#6c757d', color: 'white' }}
              >
                Aplicar
              </button>
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <label style={{ fontSize: '12px', fontWeight: 'bold' }}>Centro Costo:</label>
            <div style={{ display: 'flex', gap: '4px' }}>
              <Autocomplete
                open={bulkCcAutocompleteOpen}
                onOpen={() => setBulkCcAutocompleteOpen(true)}
                onClose={() => setBulkCcAutocompleteOpen(false)}
                value={bulkCostCenter}
                onChange={(event, newValue) => setBulkCostCenter(newValue)}
                onInputChange={(event, newInputValue, reason) => {
                  if (reason === 'input') setBulkCcSearchText(newInputValue);
                  else if (reason === 'clear') setBulkCcSearchText('');
                }}
                options={bulkCcOptions}
                getOptionLabel={(option) => `${option.codigo} - ${option.descripcion || ''}`}
                isOptionEqualToValue={(option, value) => String(option.codigo) === String(value?.codigo)}
                loading={isSearchingBulkCc}
                noOptionsText="Sin resultados"
                filterOptions={filterOptionsLimit}
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    placeholder="Buscar CC..." 
                    variant="outlined" 
                    size="small" 
                    style={{ width: '180px', backgroundColor: 'white' }}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <React.Fragment>
                          {isSearchingBulkCc ? <CircularProgress color="inherit" size={16} /> : null}
                          {params.InputProps.endAdornment}
                        </React.Fragment>
                      ),
                    }}
                  />
                )}
                componentsProps={{ popper: { style: { zIndex: 9999 } } }}
              />
              <button 
                onClick={() => handleBulkUpdate('cost_center_code', bulkCostCenter?.codigo)}
                disabled={isBulkUpdating || !bulkCostCenter}
                style={{ ...buttonStyle, margin: 0, backgroundColor: '#6c757d', color: 'white' }}
              >
                Aplicar
              </button>
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <label style={{ fontSize: '12px', fontWeight: 'bold' }}>Dept:</label>
            <div style={{ display: 'flex', gap: '4px' }}>
              <Autocomplete
                open={bulkDeptAutocompleteOpen}
                onOpen={() => setBulkDeptAutocompleteOpen(true)}
                onClose={() => setBulkDeptAutocompleteOpen(false)}
                value={bulkDepartment}
                onChange={(event, newValue) => setBulkDepartment(newValue)}
                onInputChange={(event, newInputValue, reason) => {
                  if (reason === 'input') setBulkDeptSearchText(newInputValue);
                  else if (reason === 'clear') setBulkDeptSearchText('');
                }}
                options={bulkDeptOptions}
                getOptionLabel={(option) => `${option.codigo} - ${option.descripcion || ''}`}
                isOptionEqualToValue={(option, value) => String(option.codigo) === String(value?.codigo)}
                loading={isSearchingBulkDept}
                noOptionsText="Sin resultados"
                filterOptions={filterOptionsLimit}
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    placeholder="Buscar Dept..." 
                    variant="outlined" 
                    size="small" 
                    style={{ width: '180px', backgroundColor: 'white' }}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <React.Fragment>
                          {isSearchingBulkDept ? <CircularProgress color="inherit" size={16} /> : null}
                          {params.InputProps.endAdornment}
                        </React.Fragment>
                      ),
                    }}
                  />
                )}
                componentsProps={{ popper: { style: { zIndex: 9999 } } }}
              />
              <button 
                onClick={() => handleBulkUpdate('department_code', bulkDepartment?.codigo)}
                disabled={isBulkUpdating || !bulkDepartment}
                style={{ ...buttonStyle, margin: 0, backgroundColor: '#6c757d', color: 'white' }}
              >
                Aplicar
              </button>
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <label style={{ fontSize: '12px', fontWeight: 'bold' }}>Proyecto:</label>
            <div style={{ display: 'flex', gap: '4px' }}>
              <Autocomplete
                open={bulkProjectAutocompleteOpen}
                onOpen={() => setBulkProjectAutocompleteOpen(true)}
                onClose={() => setBulkProjectAutocompleteOpen(false)}
                value={bulkProject}
                onChange={(event, newValue) => setBulkProject(newValue)}
                onInputChange={(event, newInputValue, reason) => {
                  if (reason === 'input') setBulkProjectSearchText(newInputValue);
                  else if (reason === 'clear') setBulkProjectSearchText('');
                }}
                options={bulkProjectOptions}
                getOptionLabel={(option) => `${option.codigo} - ${option.descripcion || ''}`}
                isOptionEqualToValue={(option, value) => String(option.codigo) === String(value?.codigo)}
                loading={isSearchingBulkProject}
                noOptionsText="Sin resultados"
                filterOptions={filterOptionsLimit}
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    placeholder="Buscar Proy..." 
                    variant="outlined" 
                    size="small" 
                    style={{ width: '180px', backgroundColor: 'white' }}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <React.Fragment>
                          {isSearchingBulkProject ? <CircularProgress color="inherit" size={16} /> : null}
                          {params.InputProps.endAdornment}
                        </React.Fragment>
                      ),
                    }}
                  />
                )}
                componentsProps={{ popper: { style: { zIndex: 9999 } } }}
              />
              <button 
                onClick={() => handleBulkUpdate('project_code', bulkProject?.codigo)}
                disabled={isBulkUpdating || !bulkProject}
                style={{ ...buttonStyle, margin: 0, backgroundColor: '#6c757d', color: 'white' }}
              >
                Aplicar
              </button>
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <label style={{ fontSize: '12px', fontWeight: 'bold' }}>Actividad:</label>
            <div style={{ display: 'flex', gap: '4px' }}>
              <Autocomplete
                open={bulkActivityAutocompleteOpen}
                onOpen={() => setBulkActivityAutocompleteOpen(true)}
                onClose={() => setBulkActivityAutocompleteOpen(false)}
                value={bulkActivity}
                onChange={(event, newValue) => setBulkActivity(newValue)}
                onInputChange={(event, newInputValue, reason) => {
                  if (reason === 'input') setBulkActivitySearchText(newInputValue);
                  else if (reason === 'clear') setBulkActivitySearchText('');
                }}
                options={bulkActivityOptions}
                getOptionLabel={(option) => `${option.codigo} - ${option.descripcion || ''}`}
                isOptionEqualToValue={(option, value) => String(option.codigo) === String(value?.codigo)}
                loading={isSearchingBulkActivity}
                noOptionsText="Sin resultados"
                filterOptions={filterOptionsLimit}
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    placeholder="Buscar Act..." 
                    variant="outlined" 
                    size="small" 
                    style={{ width: '180px', backgroundColor: 'white' }}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <React.Fragment>
                          {isSearchingBulkActivity ? <CircularProgress color="inherit" size={16} /> : null}
                          {params.InputProps.endAdornment}
                        </React.Fragment>
                      ),
                    }}
                  />
                )}
                componentsProps={{ popper: { style: { zIndex: 9999 } } }}
              />
              <button 
                onClick={() => handleBulkUpdate('activity_code', bulkActivity?.codigo)}
                disabled={isBulkUpdating || !bulkActivity}
                style={{ ...buttonStyle, margin: 0, backgroundColor: '#6c757d', color: 'white' }}
              >
                Aplicar
              </button>
            </div>
          </div>

          {tenantConfig?.enable_destino_field && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <label style={{ fontSize: '12px', fontWeight: 'bold' }}>Destino Masivo:</label>
              <div style={{ display: 'flex', gap: '4px' }}>
                <input 
                  type="number" 
                  value={bulkDestino} 
                  onChange={(e) => setBulkDestino(e.target.value)}
                  placeholder="Ej: 1"
                  style={{ padding: '6px', borderRadius: '4px', border: '1px solid #ccc', width: '60px' }}
                />
                <button 
                  onClick={() => handleBulkUpdate('destino', bulkDestino)}
                  disabled={isBulkUpdating}
                  style={{ ...buttonStyle, margin: 0, backgroundColor: '#6c757d', color: 'white' }}
                >
                  Aplicar
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {entries.length === 0 && !isLoading && <p>No hay asientos contables para esta factura.</p>}

      {entries.length > 0 && (
        <div style={{
          flex: '0 0 auto',
          height: isCompactViewport ? '55vh' : 'clamp(320px, 45vh, 560px)',
          minHeight: 320,
          overflow: 'auto',
          WebkitOverflowScrolling: 'touch',
          border: '1px solid #e0e0e0',
          borderRadius: 6
        }}>
          <table style={tableStyle}>
            <thead>
                <tr>
                <th style={{ ...thStyle, width: 115, position: 'sticky', top: 0, zIndex: 1 }}>Fecha</th>
                <th style={{ ...thStyle, width: 110, position: 'sticky', top: 0, zIndex: 1 }}>Cuenta</th>
                <th style={{ ...thStyle, width: 220, position: 'sticky', top: 0, zIndex: 1 }}>Nombre Cuenta</th>
                <th style={{ ...thStyle, width: 200, position: 'sticky', top: 0, zIndex: 1 }}>Descripción</th>
                <th style={{ ...thStyle, width: 120, position: 'sticky', top: 0, zIndex: 1 }}>Débito</th>
                <th style={{ ...thStyle, width: 120, position: 'sticky', top: 0, zIndex: 1 }}>Crédito</th>
                <th style={{ ...thStyle, width: 130, position: 'sticky', top: 0, zIndex: 1 }}>NIT Tercero</th>
                <th style={{ ...thStyle, width: 90, position: 'sticky', top: 0, zIndex: 1 }}>Proyecto</th>
                <th style={{ ...thStyle, width: 90, position: 'sticky', top: 0, zIndex: 1 }}>Actividad</th>
                <th style={{ ...thStyle, width: 80, position: 'sticky', top: 0, zIndex: 1 }}>CC</th>
                <th style={{ ...thStyle, width: 80, position: 'sticky', top: 0, zIndex: 1 }}>Dept</th>
                {tenantConfig?.enable_destino_field && (
                  <th style={{ ...thStyle, width: 80, position: 'sticky', top: 0, zIndex: 1 }}>Destino</th>
                )}
                <th style={{ ...thStyle, width: 200, position: 'sticky', top: 0, zIndex: 1 }}>Observaciones</th>
                <th style={{ ...thStyle, width: 120, position: 'sticky', top: 0, zIndex: 1 }}>Acciones</th>
                </tr>
            </thead>
            <tbody>
                {entries.map((entry, index) => (
                <tr 
                  key={entry.id}
                  onDragStart={(e) => handleDragStart(e, index)}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDrop={(e) => handleDrop(e, index)}
                  onDragEnd={handleDragEnd}
                  style={{
                    backgroundColor: draggedItemIndex === index ? '#f8f9fa' : (dragOverItemIndex === index ? '#e9ecef' : 'transparent'),
                    borderTop: dragOverItemIndex === index && draggedItemIndex > index ? '2px solid #0d6efd' : 'none',
                    borderBottom: dragOverItemIndex === index && draggedItemIndex < index ? '2px solid #0d6efd' : 'none',
                    opacity: draggedItemIndex === index ? 0.5 : 1
                  }}
                >
                    <td style={{ ...thTdStyle, ...(demoPresentation ? { whiteSpace: 'normal' } : cellEllipsisStyle) }}>
                      <span 
                        style={{ marginRight: '4px', color: '#ccc', cursor: 'grab', display: 'inline-block', padding: '2px 4px' }}
                        onMouseDown={(e) => { e.target.closest('tr').setAttribute('draggable', 'true'); }}
                        onMouseUp={(e) => { e.target.closest('tr').setAttribute('draggable', 'false'); }}
                        onMouseLeave={(e) => { e.target.closest('tr').setAttribute('draggable', 'false'); }}
                        title="Mantén presionado para arrastrar y reordenar"
                      >☰</span>
                      {demoPresentation ? <span style={{ whiteSpace: 'nowrap' }}>{formatColombianDate(entry.entry_date)}</span> : new Date(entry.entry_date).toLocaleDateString()}
                    </td>
                    <td style={thTdStyle}>{entry.account_code}</td>
                    <td style={{ ...thTdStyle, ...cellWrapStyle }}>{entry.account_name}</td>
                    <td style={{ ...thTdStyle, ...cellWrapStyle }}>
                      {editingDescriptionId === entry.id ? (
                        <div style={{ display: 'flex', gap: '4px' }}>
                          <input
                            type="text"
                            value={editingDescriptionValue}
                            onChange={(e) => setEditingDescriptionValue(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleUpdateDescription(entry.id, editingDescriptionValue);
                              if (e.key === 'Escape') setEditingDescriptionId(null);
                            }}
                            autoFocus
                            style={{ width: '100%', padding: '4px' }}
                            disabled={isSavingDescription}
                          />
                          <button 
                            onClick={() => handleUpdateDescription(entry.id, editingDescriptionValue)}
                            disabled={isSavingDescription}
                            style={{ padding: '2px 4px', fontSize: '10px', cursor: 'pointer' }}
                          >
                            ✓
                          </button>
                        </div>
                      ) : (
                        <div 
                          style={{ cursor: 'pointer', minHeight: '1.2em' }} 
                          onClick={() => {
                            setEditingDescriptionId(entry.id);
                            setEditingDescriptionValue(entry.description || '');
                          }}
                          title="Click para editar descripción"
                        >
                          {entry.description}
                        </div>
                      )}
                    </td>
                    <td style={{ ...thTdStyle, ...cellEllipsisStyle }}>{demoPresentation ? formatColombianCurrency(entry.debit) : entry.debit?.toLocaleString('es-CO', { style: 'currency', currency: 'COP' })}</td>
                    <td style={{ ...thTdStyle, ...cellEllipsisStyle }}>{demoPresentation ? formatColombianCurrency(entry.credit) : entry.credit?.toLocaleString('es-CO', { style: 'currency', currency: 'COP' })}</td>
                    <td style={{ ...thTdStyle, ...cellEllipsisStyle }}>{entry.third_party_nit}</td>
                    <td style={{ ...thTdStyle, ...cellEllipsisStyle }}>{entry.project_code}</td>
                    <td style={{ ...thTdStyle, ...cellEllipsisStyle }}>{entry.activity_code}</td>
                    <td style={{ ...thTdStyle, ...cellEllipsisStyle }}>{entry.cost_center_code}</td>
                    <td style={{ ...thTdStyle, ...cellEllipsisStyle }}>{entry.department_code}</td>
                    {tenantConfig?.enable_destino_field && (
                      <td style={{ ...thTdStyle, ...cellEllipsisStyle }}>{entry.destino ?? 1}</td>
                    )}
                    <td style={{ ...thTdStyle, ...cellWrapStyle }}>{demoPresentation ? explainDemoObservation(entry.observation) : entry.observation}</td>
                    <td style={thTdStyle}>
                    <button onClick={() => handleOpenModalToEdit(entry)} style={{...buttonStyle, backgroundColor: '#ffc107'}}>Editar</button>
                    <button onClick={() => handleDeleteEntry(entry.id)} style={{...buttonStyle, backgroundColor: '#dc3545', color: 'white'}} disabled={isLoading}>Eliminar</button>
                    </td>
                </tr>
                ))}
            </tbody>
            </table>
        </div>
      )}

      <AccountingEntryFormModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        entry={currentEntry}
        accountingEntries={entries}
        invoiceId={invoiceId}
        onSave={handleSaveEntry}
        tenantConfig={tenantConfig}
        erpListsStore={erpListsStore}
      />
      {isEAModalOpen && (
        <EACrossModal
          open={isEAModalOpen}
          onClose={() => setIsEAModalOpen(false)}
          invoice={currentInvoiceData}
          userId={tenantConfig?.user_id || JSON.parse(localStorage.getItem('invoice_user')).id}
          supabase={supabase}
          baseAmount={
            entries.find(e => Number(e.debit) > 0 && e.account_code !== tenantConfig?.ea_bridge_account)?.debit || 0
          }
          onApplyCross={handleEACrossApply}
        />
      )}
    </div>
  );
};

export default AccountingEntries;
